/**
 * useContentRefresh — refetch wall/summary data when instance content changes.
 *
 * Listens for `instance-content-changed` socket events filtered by instanceId
 * and resource list. Coalesces bursts into a single refetch, and enforces a
 * minimum interval between refreshes (default 60s) so a table of iPads
 * submitting at once doesn't hammer the API.
 *
 * Pattern mirrors SWOTView's socket + throttle approach.
 */
import { useEffect, useRef } from 'react';
import SocketService from '../../../../packages/shared/socket';

/**
 * @param {string|null|undefined} instanceId
 * @param {string[]} resources - e.g. ['lens_items', 'solutions']
 * @param {() => void|Promise<void>} refetch
 * @param {{ minIntervalMs?: number, enabled?: boolean }} [options]
 */
export default function useContentRefresh(
  instanceId,
  resources,
  refetch,
  { minIntervalMs = 60000, enabled = true } = {}
) {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const resourcesRef = useRef(resources);
  resourcesRef.current = resources;

  const lastRefreshAtRef = useRef(0);
  const pendingTimerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !instanceId) return;

    const socket = SocketService.socket;
    if (!socket) return;

    const doRefresh = () => {
      lastRefreshAtRef.current = Date.now();
      pendingTimerRef.current = null;
      try {
        refetchRef.current?.();
      } catch (err) {
        console.error('[useContentRefresh] refetch error:', err);
      }
    };

    const scheduleRefresh = () => {
      const elapsed = Date.now() - lastRefreshAtRef.current;
      if (elapsed >= minIntervalMs) {
        if (pendingTimerRef.current) {
          clearTimeout(pendingTimerRef.current);
          pendingTimerRef.current = null;
        }
        doRefresh();
        return;
      }
      // Coalesce: only one pending timer; fire at the earliest allowed time
      if (pendingTimerRef.current) return;
      const wait = Math.max(0, minIntervalMs - elapsed);
      pendingTimerRef.current = setTimeout(doRefresh, wait);
    };

    const handleChange = (data) => {
      if (!data || data.instanceId !== instanceId) return;
      const watched = resourcesRef.current;
      if (watched?.length && data.resource && !watched.includes(data.resource)) {
        return;
      }
      scheduleRefresh();
    };

    // Re-joining is idempotent — safe alongside ScreenClient's own join
    socket.emit('join-instance', { instanceId, deviceType: 'display' });
    socket.on('instance-content-changed', handleChange);

    return () => {
      socket.off('instance-content-changed', handleChange);
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, [instanceId, minIntervalMs, enabled]);
}
